import type { Article } from '../types';
import { h2, h3, p, ul, callout, code, table } from '../b';

const article: Article = {
  slug: 'telegram-stars-bot-api-developer-guide',
  title: 'Telegram Stars Bot API Guide: sendInvoice, XTR, Webhooks, Subscriptions and Refunds',
  metaTitle: 'Telegram Stars Bot API Guide: sendInvoice, XTR & Refunds',
  metaDescription:
    'Developer guide to Telegram Stars payments with the Bot API: createInvoiceLink with currency XTR, pre_checkout_query, successful_payment, subscriptions, refundStarPayment and getStarTransactions, with code examples.',
  keywords: [
    'telegram stars bot api',
    'telegram sendinvoice xtr',
    'createinvoicelink stars',
    'telegram stars payment bot',
    'refundstarpayment',
    'telegram bot payments stars',
    'telegram stars subscription api',
  ],
  category: 'Developers',
  datePublished: '2026-09-19',
  dateModified: '2026-10-12',
  readingTimeMin: 10,
  excerpt:
    'Everything a developer needs to accept Telegram Stars in a bot or Mini App: the XTR currency, invoice creation, the pre-checkout handshake, successful_payment handling, Stars subscriptions, refunds, transaction history, and the pitfalls that break payments in production.',
  keyTakeaways: [
    'Stars payments use the standard Bot Payments API with currency "XTR" and an empty provider_token; prices are a single LabeledPrice whose amount is the number of Stars.',
    'You must answer every pre_checkout_query within 10 seconds with answerPreCheckoutQuery, otherwise the payment fails.',
    'Persist telegram_payment_charge_id from successful_payment: it is required for refundStarPayment and for reconciliation with getStarTransactions.',
    'Subscriptions are created by passing subscription_period=2592000 (30 days) to createInvoiceLink; Mini Apps open invoices with WebApp.openInvoice.',
    'If you would rather not build and operate all of this, Telegram Stars Payment Platform exposes a REST API and webhooks on top of a ready-made ledger and payout flow.',
  ],
  body: [
    h2('Prerequisites'),
    ul([
      'A bot token from [@BotFather](https://t.me/BotFather). No payment provider needs to be connected for Stars.',
      'A server (or serverless function) able to receive updates via webhook or long polling.',
      'Any Bot API library: `python-telegram-bot`, `aiogram`, `grammY`, `Telegraf`, `node-telegram-bot-api`, or raw HTTP calls.',
    ]),

    h2('1. Create a Stars invoice'),
    p('Two methods create invoices. `sendInvoice` posts the invoice into a chat; `createInvoiceLink` returns a URL you can share anywhere or open from a Mini App. The parameters that matter for Stars:'),
    table(
      ['Parameter', 'Value for Stars'],
      [
        ['`currency`', '`"XTR"`'],
        ['`provider_token`', 'Empty string (omit or `""`)'],
        ['`prices`', 'Exactly one `LabeledPrice`; `amount` is the number of Stars (no cents)'],
        ['`payload`', 'Your internal order ID (1–128 bytes). Never trust it blindly; verify server-side'],
        ['`title` / `description`', 'Shown on the payment sheet (title ≤ 32 chars, description ≤ 255)'],
        ['`photo_url`', 'Optional product image'],
      ],
    ),
    h3('Example: createInvoiceLink with raw HTTP'),
    code(
      `POST https://api.telegram.org/bot<TOKEN>/createInvoiceLink
Content-Type: application/json

{
  "title": "Premium Access",
  "description": "30 days of premium content",
  "payload": "order_7f3a9c",
  "currency": "XTR",
  "prices": [{ "label": "Premium Access", "amount": 250 }]
}`,
      'http',
    ),
    h3('Example: sendInvoice with python-telegram-bot'),
    code(
      `from telegram import LabeledPrice

await context.bot.send_invoice(
    chat_id=update.effective_chat.id,
    title="Premium Access",
    description="30 days of premium content",
    payload="order_7f3a9c",
    provider_token="",          # empty for Telegram Stars
    currency="XTR",
    prices=[LabeledPrice("Premium Access", 250)],
)`,
      'python',
    ),
    h3('Example: createInvoiceLink with grammY (Node.js)'),
    code(
      `const link = await bot.api.createInvoiceLink(
  "Premium Access",
  "30 days of premium content",
  "order_7f3a9c",
  "",            // provider_token: empty for Stars
  "XTR",
  [{ label: "Premium Access", amount: 250 }],
);
// share \`link\` or open it from a Mini App`,
      'typescript',
    ),

    h2('2. Answer the pre-checkout query'),
    p(
      'When the user taps Pay, Telegram sends your bot an update containing `pre_checkout_query`. This is your last chance to validate the order (stock, user eligibility, payload integrity). You **must** reply within 10 seconds:',
    ),
    code(
      `@router.pre_checkout_query()
async def on_pre_checkout(q: PreCheckoutQuery):
    if not order_exists(q.invoice_payload):
        await q.answer(ok=False, error_message="Order not found")
        return
    await q.answer(ok=True)`,
      'python',
    ),
    callout('The 10-second limit includes network time. Keep this handler free of slow external calls; do validation against your own database only.'),

    h2('3. Handle successful_payment'),
    p('After a successful charge Telegram sends a service message with the `successful_payment` field. Key fields:'),
    ul([
      '`currency` (`"XTR"`) and `total_amount` (Stars).',
      '`invoice_payload`: your order ID.',
      '`telegram_payment_charge_id`: **store this**; it identifies the transaction for refunds and reconciliation.',
      '`is_recurring`, `is_first_recurring`, `subscription_expiration_date` for subscriptions.',
    ]),
    code(
      `bot.on("message:successful_payment", async (ctx) => {
  const sp = ctx.message.successful_payment;
  await db.orders.markPaid(sp.invoice_payload, {
    chargeId: sp.telegram_payment_charge_id,
    stars: sp.total_amount,
    userId: ctx.from.id,
  });
  await deliverProduct(ctx.from.id, sp.invoice_payload);
});`,
      'typescript',
    ),
    p('Make the handler **idempotent**: Telegram may redeliver updates, so check whether the order is already fulfilled before delivering twice.'),

    h2('4. Stars subscriptions'),
    p(
      'Recurring billing is built in. Pass `subscription_period` to `createInvoiceLink`; the only supported value is **2592000 seconds (30 days)**. Telegram charges the user automatically each period and sends a new `successful_payment` with `is_recurring=true`. Use `editUserStarSubscription` to cancel or re-enable a subscription on the user\'s behalf. Users can also manage subscriptions from their Telegram settings.',
    ),
    code(
      `const link = await bot.api.createInvoiceLink(
  "Premium Monthly",
  "Renews every 30 days",
  "sub_user_42",
  "",
  "XTR",
  [{ label: "Premium Monthly", amount: 150 }],
  { subscription_period: 2592000 },
);`,
      'typescript',
    ),

    h2('5. Refunds'),
    p('Refund any Stars payment with `refundStarPayment(user_id, telegram_payment_charge_id)`. The Stars return to the user and the transaction is reversed in your Stars balance. Refunds are possible even after delivery, so define a clear policy.'),
    code(
      `await bot.api.refundStarPayment(userId, chargeId);`,
      'typescript',
    ),

    h2('6. Reconciliation with getStarTransactions'),
    p(
      '`getStarTransactions(offset, limit)` returns the bot\'s incoming and outgoing Stars transactions (payments, refunds, withdrawals). Run it periodically and compare it with your database; discrepancies usually mean a missed update. `getMyStarBalance` returns the current Stars balance of the bot.',
    ),

    h2('7. Stars payments from a Mini App'),
    p('In a Mini App, request an invoice link from your backend, then open it from the front end:'),
    code(
      `const url = await fetch("/api/invoice", { method: "POST" }).then(r => r.json());
window.Telegram.WebApp.openInvoice(url, (status) => {
  if (status === "paid") showSuccess();
  else if (status === "cancelled") showCancelled();
  else showFailed();
});`,
      'javascript',
    ),
    p('The `successful_payment` update still arrives at your bot; treat it as the source of truth, not the client callback.'),

    h2('8. Withdrawing the Stars your bot earns'),
    p(
      'Stars accumulate in the bot\'s balance and become withdrawable after 21 days. Withdraw to TON through Fragment (minimum 1,000 Stars) or spend them on Telegram Ads. See [How to withdraw Telegram Stars to TON](/blog/withdraw-telegram-stars-to-ton).',
    ),

    h2('Common pitfalls'),
    ul([
      '**Non-empty `provider_token`** with `XTR` → `Bad Request`. Leave it empty.',
      '**Multiple price items** → rejected. Stars invoices accept exactly one `LabeledPrice`.',
      '**Slow pre-checkout handler** → payment fails after 10 seconds. Validate locally.',
      '**Not storing `telegram_payment_charge_id`** → you cannot refund later.',
      '**Trusting the Mini App callback** instead of the `successful_payment` update → fraud risk.',
      '**Non-idempotent delivery** → duplicate fulfilment on redelivered updates.',
    ]),

    h2('Build or buy?'),
    p(
      'The Bot API gives you full control, but a production-grade Stars business also needs a ledger, maturity tracking, payout management, refunds UI, analytics and multi-user support. **Telegram Stars Payment Platform** provides all of this out of the box, with a REST API (create invoices, list payments, request withdrawals) and webhooks for payment events, for a flat 3% fee. Many developers use it as their payment back office while keeping their own bot for the user-facing experience. [See how it works](/how-it-works).',
    ),
  ],
  faq: [
    {
      q: 'What currency code do I use for Telegram Stars in the Bot API?',
      a: 'Use "XTR" as the currency and leave provider_token empty. Prices must contain exactly one LabeledPrice whose amount is the number of Stars.',
    },
    {
      q: 'Can a Telegram bot charge in Stars without a payment provider?',
      a: 'Yes. Stars are processed by Telegram itself; no Stripe or other provider token is needed.',
    },
    {
      q: 'How do I refund a Telegram Stars payment?',
      a: 'Call refundStarPayment with the user_id and the telegram_payment_charge_id from the successful_payment message.',
    },
    {
      q: 'How do Telegram Stars subscriptions work in the API?',
      a: 'Create an invoice link with subscription_period set to 2592000 (30 days). Telegram renews automatically and sends a successful_payment with is_recurring=true for each period.',
    },
    {
      q: 'Can I test Telegram Stars payments?',
      a: 'Use the Telegram test environment (test DC) with a bot created there; Stars in the test environment are free. In production, start with a low-priced invoice and refund it.',
    },
  ],
  relatedSlugs: [
    'telegram-mini-app-stars-payments',
    'telegram-stars-subscriptions',
    'telegram-stars-refunds-21-day-hold',
    'how-to-accept-telegram-stars-payments',
  ],
};

export default article;
